/**
 * 콘솔 셸.
 *
 * 라우팅은 **해시**로 한다. 콘솔은 세 가지 방식으로 배포된다(독립 웹 · iframe 임베드
 * · 호스트 번들). 경로 라우팅을 쓰면 그때마다 호스트 쪽에 rewrite 규칙을 요구하게
 * 되고, 규칙이 빠지면 새로고침이 404 가 된다. 해시는 어디에 놓든 그냥 동작한다.
 *
 * 여기서 **로그인 여부에 따라 무엇을 그릴지**도 정한다. 신원을 확인하기 전에
 * 콘솔을 그리면 빈 목록이 잔뜩 뜨고, 사용자는 그것을 "데이터가 없다"로 읽는다.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { HashRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ApiCallError, SignageOneClient, type AuthState, type Me } from './api/client.js';
import type { Transport } from './api/transport.js';
import { describeError } from './hooks.js';
import { AdminLayout } from './components/layout/AdminLayout.js';
import { canOpen, navCapabilities } from './nav.js';
import { DashboardScreen } from './screens/Dashboard.js';
import { DisplaysScreen } from './screens/Displays.js';
import { GroupsScreen } from './screens/Groups.js';
import { LocationsScreen } from './screens/Locations.js';
import { MonitoringScreen } from './screens/Monitoring.js';
import { HubScreen } from './screens/Hub.js';
import { AssetsScreen, PlaylistsScreen } from './screens/Content.js';
import { ContentDetailScreen } from './screens/ContentDetail.js';
import { DisplayDetailScreen } from './screens/DisplayDetail.js';
import { CampaignsScreen, SchedulesScreen } from './screens/Scheduling.js';
import { ReportsScreen } from './screens/Reports.js';
import { ActivitiesScreen } from './screens/Activities.js';
import { SystemScreen } from './screens/System.js';
import { UsersScreen } from './screens/Users.js';
import { SettingsScreen } from './screens/Settings.js';
import { BrandProvider } from './components/brand.js';
import { setMediaSource } from './components/mediaSource.js';
import { AgentInstallScreen } from './screens/AgentInstall.js';
import { AgentDownloadScreen } from './screens/AgentDownload.js';
import { AccountScreen } from './screens/Account.js';
import { ForcedPasswordChange, LoginScreen } from './screens/Login.js';
import { DocScreen, DocsHomeScreen } from './screens/Docs.js';
import { ApiSpecScreen } from './screens/ApiSpec.js';
import { InspectionScreen } from './screens/Inspection.js';
import { DeliveriesScreen } from './screens/Deliveries.js';
import { ServerPackagesScreen } from './screens/ServerPackages.js';
import { AlertsScreen } from './screens/Alerts.js';
import { PluginsScreen } from './screens/Plugins.js';
import { ServerPackageManualScreen } from './screens/ServerPackageManual.js';
import { ManualChapterScreen, ManualHomeScreen } from './screens/Manual.js';
import { OverviewScreen } from './screens/Overview.js';
import { ConfirmHost } from './components/ConfirmDialog.js';
import { ToastHost } from './components/Toast.js';

type Phase =
  | { kind: 'loading' }
  /**
   * 신원이 없다. 로컬 계정이 켜져 있으면 로그인 화면을 그린다.
   * `expired` 는 쓰던 도중 끊겨서 온 것이다 — 로그인 화면이 그 사실을 한 줄로 알린다.
   */
  | { kind: 'anonymous'; state: AuthState; expired?: boolean }
  | { kind: 'signed-in'; me: Me }
  | { kind: 'error'; message: string };

export function App({ transport }: { transport: Transport }): ReactNode {
  const client = useMemo(() => new SignageOneClient(transport), [transport]);
  // 올린 HTML·반반 화면 템플릿이 품은 그림·영상·HTML — 미리보기가 묻으면 콘솔 미리보기 주소(HTML 은 기준 크기도)로 받아 넘긴다(`mediaSource.ts`).
  useEffect(() => {
    setMediaSource((id) => client.getAsset(id));
    return () => setMediaSource(null);
  }, [client]);
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  /**
   * 신원 확인.
   *
   * `me` 와 `auth/state` 를 함께 부른다. 401 일 때 로그인 화면을 그리려면 **어떤
   * 로그인 수단이 있는지**를 알아야 하고, 그건 인증 없이 조회할 수 있다.
   */
  const load = useCallback(async (expired = false): Promise<void> => {
    const [me, state] = await Promise.allSettled([client.me(), client.authState()]);

    if (me.status === 'fulfilled') {
      setPhase({ kind: 'signed-in', me: me.value });
      return;
    }

    const unauthorized = me.reason instanceof ApiCallError && me.reason.status === 401;
    if (unauthorized && state.status === 'fulfilled') {
      setPhase({ kind: 'anonymous', state: state.value, expired });
      return;
    }

    setPhase({ kind: 'error', message: describeError(me.reason) });
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * 쓰는 도중 로그인이 끊기면(다른 기기에서 모두 로그아웃, 계정 비활성화·재설정) 신원을 다시
   * 확인해 로그인 화면으로 간다. 화면 여러 곳이 같은 순간 401 을 받으므로 한 번만 확인한다.
   */
  const recheck = useRef(false);
  useEffect(() => {
    client.onUnauthorized = () => {
      if (recheck.current) return;
      recheck.current = true;
      void load(true).finally(() => {
        recheck.current = false;
      });
    };
    return () => {
      client.onUnauthorized = null;
    };
  }, [client, load]);

  const signOut = useCallback((): void => {
    // 실패해도 화면은 로그아웃 상태로 돌린다 — 서버 세션은 만료로 정리된다.
    void client.logout().finally(() => {
      setPhase({ kind: 'loading' });
      void load();
    });
  }, [client, load]);

  if (phase.kind === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-th-1">
        <p className="text-[13.5px] text-slate-400">불러오는 중…</p>
      </div>
    );
  }

  if (phase.kind === 'error') {
    return (
      <Centered>
        <h1 className="text-[15px] font-semibold text-slate-900">서버에 연결하지 못했습니다</h1>
        <p className="mt-2 text-[13.5px] text-slate-500">{phase.message}</p>
        <p className="mt-3 font-mono text-[11.5px] uppercase tracking-wide text-slate-400">
          transport: {transport.kind}
        </p>
        <button type="button" className="btn btn-primary mt-4" onClick={() => void load()}>
          다시 시도
        </button>
      </Centered>
    );
  }

  if (phase.kind === 'anonymous') {
    if (!phase.state.local_accounts) {
      return (
        <Centered>
          <h1 className="text-[15px] font-semibold text-slate-900">로그인이 필요합니다</h1>
          <p className="mt-2 text-[13.5px] text-slate-500">
            이 서버는 로컬 계정을 쓰지 않습니다. 연동된 서비스에서 콘솔을 열거나, 서비스 토큰으로
            접속하세요.
          </p>
          <button type="button" className="btn btn-primary mt-4" onClick={() => void load()}>
            다시 확인
          </button>
        </Centered>
      );
    }
    return (
      <LoginScreen client={client} state={phase.state} expired={phase.expired} onSignedIn={() => void load()} />
    );
  }

  // 임시 비밀번호 상태 — 서버가 다른 API 를 전부 막는다. 화면도 그것만 보여준다.
  if (phase.me.account?.must_change_password) {
    return (
      <ForcedPasswordChange
        client={client}
        email={phase.me.account.email}
        onChanged={() => void load()}
        onSignOut={signOut}
      />
    );
  }

  const me = phase.me;
  /** 이 주소를 열 수 있는가 — 사이드바와 같은 표를 본다. */
  const allow = (path: string): boolean => canOpen(path, navCapabilities(me));

  return (
    <HashRouter>
      <Routes>
        {/* 브랜드 — 템플릿 미리보기가 어디서나 같은 머리글·색·글꼴로 그리게(`components/brand.tsx`). */}
        <Route
          element={
            <BrandProvider client={client}>
              <AdminLayout client={client} transport={transport} me={me} onSignOut={signOut} />
            </BrandProvider>
          }
        >
          <Route path="/" element={<DashboardScreen client={client} me={me} />} />
          <Route path="/content" element={<AssetsScreen client={client} me={me} />} />
          <Route path="/content/:id" element={<ContentDetailScreen client={client} me={me} />} />
          <Route path="/displays" element={<DisplaysScreen client={client} me={me} />} />
          {/*
            화면 상세는 능력을 가리지 않는다 — 누구나 열어 "왜 이게 나오나"를 볼 수 있다.
            다만 안에 파괴적인 동작(캐시 파일 삭제)이 있어 `me` 를 넘긴다. 화면을 가리는
            것이 아니라 그 버튼 하나를 가리기 위한 것이다.
          */}
          <Route path="/displays/:id" element={<DisplayDetailScreen client={client} me={me} />} />
          <Route path="/groups" element={<GroupsScreen client={client} me={me} />} />
          <Route path="/locations" element={<LocationsScreen client={client} me={me} />} />
          <Route path="/wall" element={<MonitoringScreen client={client} me={me} view="board" />} />
          {allow('/integrated') ? (
            <Route path="/integrated" element={<MonitoringScreen client={client} me={me} view="map" />} />
          ) : null}
          {allow('/hub') ? <Route path="/hub" element={<HubScreen client={client} capabilities={me.capabilities} />} /> : null}
          <Route path="/playlists" element={<PlaylistsScreen client={client} me={me} />} />
          <Route path="/schedules" element={<SchedulesScreen client={client} me={me} />} />
          <Route path="/campaigns" element={<CampaignsScreen client={client} me={me} />} />
          {/*
            권한이 없으면 라우트 자체를 두지 않는다. 주소를 직접 쳐도 대시보드로
            떨어진다 — 서버가 막아 주지만, 403 화면을 보여줄 이유가 없다.

            **필요한 능력은 사이드바와 같은 표에서 읽는다**(`canOpen`). 라우터에 따로 적어
            두면 메뉴와 조용히 어긋나 "메뉴에는 없는데 주소로는 열리는" 화면이 생긴다.
          */}
          {allow('/reports') ? <Route path="/reports" element={<ReportsScreen client={client} />} /> : null}
          {allow('/activities') ? (
            <Route path="/activities" element={<ActivitiesScreen client={client} />} />
          ) : null}
          <Route path="/account" element={<AccountScreen client={client} me={me} />} />
          {/* 고객이 받는 자리 — 롤아웃 없는 내려받기 전용(`fleet:write`). */}
          {allow('/agent/download') ? (
            <Route path="/agent/download" element={<AgentDownloadScreen client={client} me={me} />} />
          ) : null}
          {allow('/agent') ? (
            <Route path="/agent" element={<AgentInstallScreen client={client} me={me} />} />
          ) : null}
          {allow('/users') ? <Route path="/users" element={<UsersScreen client={client} me={me} />} /> : null}
          {allow('/settings') ? <Route path="/settings" element={<SettingsScreen client={client} />} /> : null}
          {allow('/system') ? (
            <Route path="/system" element={<SystemScreen client={client} transport={transport} me={me} />} />
          ) : null}
          {allow('/alerts') ? <Route path="/alerts" element={<AlertsScreen client={client} />} /> : null}
          {allow('/plugins') ? <Route path="/plugins" element={<PluginsScreen client={client} />} /> : null}
          {allow('/server-packages') ? (
            <Route path="/server-packages" element={<ServerPackagesScreen client={client} />} />
          ) : null}
          {/* 설치 매뉴얼은 서버 설치 파일 메뉴의 탭이라 같은 능력으로 가린다. */}
          {allow('/server-packages') ? (
            <Route path="/server-packages/manual" element={<ServerPackageManualScreen client={client} />} />
          ) : null}
          {/*
            출하 검사는 능력으로 가린다 — API 정의·시스템 문서와 다른 점이다. 저쪽은 누가
            읽어도 되는 설명이지만, 이것은 고객사·검사자 이름과 판정이 적히는 납품 문서다.
          */}
          {allow('/inspection') ? (
            <Route path="/inspection" element={<InspectionScreen client={client} me={me} />} />
          ) : null}
          {/* 납품 이력도 능력으로 가린다 — 다른 고객사 이름이 적히는 목록이다. */}
          {allow('/deliveries') ? <Route path="/deliveries" element={<DeliveriesScreen client={client} />} /> : null}
          {/*
            API 정의도 능력을 가리지 않는다. 무엇을 부를 수 있는지는 누구나 읽어도 되고,
            실제 관문은 서비스 토큰이다 — 이 화면은 토큰을 발급하지도, 호출을 대신
            보내지도 않는다. 필요한 능력은 항목마다 적혀 있다.
          */}
          <Route path="/api-spec" element={<ApiSpecScreen client={client} />} />
          {/*
            사용 설명서는 능력을 가리지 않는다. 권한이 없어 메뉴가 안 보이는 사람일수록
            "왜 없나" 를 읽어야 한다. 장마다 권한 안내와 "이 화면 열기" 가림은 화면이 한다.
          */}
          <Route path="/manual" element={<ManualHomeScreen />} />
          {/* 기능 표식(feature:hub)까지 — 메뉴가 그것으로 가려지므로 장 머리의 안내와 [이 화면 열기] 도 같은 값을 본다. */}
          <Route path="/manual/:slug" element={<ManualChapterScreen capabilities={navCapabilities(me)} />} />
          {/* 시스템 소개도 능력을 가리지 않는다 — 고객 앞에서 누구의 계정으로든 열어 보여 주는 화면이다. */}
          <Route path="/overview" element={<OverviewScreen />} />
          {/*
            시스템 문서는 능력을 가리지 않는다 — 이 시스템이 무엇인지는 누구나 읽어도 된다.
            문서 하나하나의 가림막은 목록(`docs/registry.ts`)이 갖고 있다.
          */}
          <Route path="/docs" element={<DocsHomeScreen capabilities={me.capabilities} />} />
          <Route path="/docs/:slug" element={<DocScreen capabilities={me.capabilities} />} />
          {/* 모르는 경로는 대시보드로. 북마크가 깨져도 빈 화면을 보여주지 않는다. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      {/* 확인 창 — 어느 화면에서 `confirmDialog()` 를 불러도 여기 하나가 그린다. */}
      <ConfirmHost />
      {/* 완료 알림 — 저장·삭제 뒤의 한 줄(`notify`). */}
      <ToastHost />
    </HashRouter>
  );
}

function Centered({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="flex min-h-screen items-center justify-center bg-th-1 p-6">
      <div className="max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
        {children}
      </div>
    </div>
  );
}
